/**
 * Serializes a value to JSON that is safe to embed inside a `<script>` element.
 *
 * `JSON.stringify` alone is not safe here. Script element content is raw text:
 * the browser does not HTML-entity-decode it, it only scans for the literal
 * `</script` sequence to close the element. So a string containing
 * `</script><script>alert(1)</script>` breaks out of the tag and executes.
 *
 * HTML entities are the wrong fix here: `&lt;` would not decode back to `<` for
 * `JSON.parse`, and escaping `"` would corrupt the JSON outright. Instead the
 * offending characters are emitted as JSON `\uXXXX` escapes, which `JSON.parse`
 * restores to the original characters while never producing `</script` in the
 * markup.
 *
 * U+2028 and U+2029 are legal unescaped inside JSON strings but are line
 * terminators in JavaScript source, so they break a script that is evaluated
 * rather than parsed as JSON. Escaping them keeps the output safe for both.
 */
const SCRIPT_UNSAFE_PATTERN = /[<>&\u2028\u2029]/g

const SCRIPT_ESCAPES: Record<string, string> = {
  '<': '\\u003c',
  '>': '\\u003e',
  '&': '\\u0026',
  '\u2028': '\\u2028',
  '\u2029': '\\u2029',
}

const serializeForScriptTag = (value: unknown, space?: number): string =>
  JSON.stringify(value, null, space).replace(
    SCRIPT_UNSAFE_PATTERN,
    (char) => SCRIPT_ESCAPES[char] ?? char
  )

export default serializeForScriptTag
